import { useForm } from "react-hook-form";
import { FormContainer, SelectInput, TextInput } from "../../shared";
import { RegexPattern, clearCache } from "../../../utils";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import {
    VEEVA_CONFIG_COUNTRY_TYPES,
    VEEVA_CONFIG_IDENTITY_TYPE,
    VEEVA_INSTANCE_TYPES,
} from "../../../utils/appList";

const VeevaConfigForm = ({ formType, query, formData }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ...formData,
            veevaSpecilityFilter:
                formType === "update" && typeof formData.veevaSpecilityFilter === "string"
                    ? formData.veevaSpecilityFilter.replaceAll("%", "")
                    : "",
        },
    });

    const veevaInstanceType = watch("veevaInstanceType");

    const { submitMutation, loading } = useGlobalMutation(query, (cache) => {
        clearCache(cache, "allVeevaEnvironmentConfigurations");
    });

    const handleSubmission = async (data) => {
        const {
            id,
            country,
            countryType,
            aemCountryNode,
            aemLanguageNode,
            identityType,
            identityTypeName,
            salesforceCountryCode,
            veevaSpecilityFilter,
            veevaInstanceType,
        } = data;

        const defaultVariables = {
            veevaEnvironmentConfiguration: {
                country,
                countryType: Number(countryType),
                aemCountryNode,
                aemLanguageNode,
                identityType,
                identityTypeName,
                salesforceCountryCode,
                veevaSpecilityFilter:
                    veevaInstanceType === "europe" ? `%${veevaSpecilityFilter}%` : null,
                veevaInstanceType,
            },
        };

        const variables =
            formType === "update"
                ? {
                      id,
                      ...defaultVariables,
                  }
                : {
                      ...defaultVariables,
                  };

        const dataPath =
            formType === "update"
                ? "updateVeevaEnvironmentConfigurationById.veevaEnvironmentConfiguration"
                : "createVeevaEnvironmentConfiguration.veevaEnvironmentConfiguration";

        const isResultNull = await submitMutation(
            variables,
            dataPath,
            `Successfully ${formType === "update" ? "Updated" : "Created"}`
        );
        isResultNull === null && formType !== "update" && reset();
    };

    return (
        <FormContainer
            submitAction={handleSubmit(handleSubmission)}
            loading={loading}
            submitButtonText={`${formType === "update" ? "Update" : "Create"} ICC Config`}
            submitBtnSm={formType === "update"}
        >
            <div className="row">
                <div className="col-lg-6">
                    <SelectInput
                        id="veevaInstanceType"
                        labelText="Veeva Instance Type"
                        options={VEEVA_INSTANCE_TYPES}
                        {...register("veevaInstanceType", {
                            required: true,
                        })}
                        isInvalid={!!errors.veevaInstanceType}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="country"
                        type="text"
                        labelText="Country"
                        {...register("country", {
                            pattern: RegexPattern.name,
                            required: true,
                        })}
                        isInvalid={!!errors.country}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <SelectInput
                        id="countryType"
                        labelText="Country Type"
                        options={VEEVA_CONFIG_COUNTRY_TYPES}
                        {...register("countryType", {
                            required: true,
                        })}
                        isInvalid={!!errors.countryType}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="aemCountryNode"
                        type="text"
                        labelText="AEM Country Node"
                        {...register("aemCountryNode", {
                            pattern: RegexPattern.textWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.aemCountryNode}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="aemLanguageNode"
                        type="text"
                        labelText="AEM Language Node"
                        {...register("aemLanguageNode", {
                            pattern: RegexPattern.textWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.aemLanguageNode}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <SelectInput
                        id="identityType"
                        labelText="Identity Type"
                        options={VEEVA_CONFIG_IDENTITY_TYPE}
                        {...register("identityType", {
                            required: true,
                        })}
                        isInvalid={!!errors.identityType}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="identityTypeName"
                        type="text"
                        labelText="Identity Type Name"
                        {...register("identityTypeName", {
                            pattern: RegexPattern.nameWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.identityTypeName}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="salesforceCountryCode"
                        type="text"
                        labelText="Salesforce Country Code"
                        {...register("salesforceCountryCode", {
                            pattern: RegexPattern.nameWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.salesforceCountryCode}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <TextInput
                        id="veevaSpecilityFilter"
                        type="text"
                        labelText="Veeva Specility Filter"
                        {...register("veevaSpecilityFilter", {
                            pattern: /^[a-zA-Z_.]*$/,
                            required: veevaInstanceType === "europe",
                        })}
                        isInvalid={veevaInstanceType === "europe" && !!errors.veevaSpecilityFilter}
                        required={veevaInstanceType === "europe"}
                        disabled={veevaInstanceType !== "europe"}
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default VeevaConfigForm;
